import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Container, Pagination, PaginationItem, PaginationLink } from "reactstrap";
import { Download, RefreshCw } from "react-feather";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import { getSmsUsageSummary, getSmsLogs } from "../../utils/smsUsageApi";
import { exportToCsv } from "../../utils/exportCsv";
import { C, tbl, reportingGlobalCSS } from "./reportingStyles";
import { RCard, Loader as RLoader } from "./ReportingShared";

const PROVIDER_OPTIONS = [
  { value: "", label: "All Providers" },
  { value: "MSEGAT", label: "Msegat" },
  { value: "AUTHENTICA", label: "Authentica" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "sent", label: "Sent" },
  { value: "failed", label: "Failed" },
  { value: "no_provider", label: "No Provider" },
];

const PURPOSE_OPTIONS = [
  { value: "", label: "All Purposes" },
  { value: "login", label: "Login OTP" },
  { value: "phoneChangeCurrent", label: "Phone Change (current)" },
  { value: "phoneChangeNew", label: "Phone Change (new)" },
  { value: "publicWaitlist", label: "Waitlist" },
  { value: "rewardRedemption", label: "Reward Redemption" },
  { value: "walletTransfer", label: "Wallet Transfer" },
  { value: "unknown", label: "Unknown" },
];

const PURPOSE_LABEL = PURPOSE_OPTIONS.reduce(
  (acc, o) => (o.value ? { ...acc, [o.value]: o.label } : acc),
  {}
);

const STATUS_COLOR = {
  sent: { bg: C.greenLight, fg: C.green },
  failed: { bg: C.coralLight, fg: C.coral },
  no_provider: { bg: C.orangeLight, fg: C.orange },
};

const Pill = ({ status }) => {
  const c = STATUS_COLOR[status] || { bg: C.bg, fg: C.textMed };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "4px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        background: c.bg,
        color: c.fg,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.fg }} />
      {(status || "").replace(/_/g, " ").toUpperCase()}
    </span>
  );
};

const filterSelectStyle = {
  padding: "7px 12px",
  fontSize: 12,
  borderRadius: 8,
  border: `1.5px solid ${C.border}`,
  fontFamily: "inherit",
  outline: "none",
  color: C.text,
  background: C.white,
  cursor: "pointer",
  minWidth: 150,
};

const filterInputStyle = {
  ...filterSelectStyle,
  cursor: "text",
  minWidth: 140,
};

const tabBtnStyle = (active) => ({
  padding: "7px 16px",
  fontSize: 12,
  fontWeight: 700,
  borderRadius: 8,
  border: `1.5px solid ${active ? C.coral : C.border}`,
  background: active ? C.coralLight : C.white,
  color: active ? C.coral : C.textMed,
  cursor: "pointer",
  transition: "all 0.15s",
});

const IconBtn = ({ title, onClick, Icon }) => (
  <div
    onClick={onClick}
    title={title}
    style={{
      width: 36,
      height: 36,
      borderRadius: 10,
      border: `1.5px solid ${C.border}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      background: C.white,
      flexShrink: 0,
      transition: "all 0.15s",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.borderColor = C.coral;
      e.currentTarget.style.background = C.coralLight;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = C.border;
      e.currentTarget.style.background = C.white;
    }}
  >
    <Icon size={15} color={C.textMed} />
  </div>
);

const toDateInput = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const SmsUsage = () => {
  const { selectedMerchant } = useSelector((state) => state.merchant || {});

  const now = new Date();
  const [tab, setTab] = useState("summary");
  const [filters, setFilters] = useState({
    provider: "",
    dateFrom: toDateInput(new Date(now.getFullYear(), now.getMonth(), 1)),
    dateTo: toDateInput(now),
  });

  // Summary state
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryRows, setSummaryRows] = useState([]);

  // Logs state
  const [logsLoading, setLogsLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logFilters, setLogFilters] = useState({ status: "", purpose: "", search: "", limit: 20 });
  const [searchText, setSearchText] = useState("");
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const baseParams = useCallback(
    () => ({
      ...(selectedMerchant && selectedMerchant !== "all" && { MerchantId: selectedMerchant }),
      ...(filters.provider && { provider: filters.provider }),
      ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
      ...(filters.dateTo && { dateTo: `${filters.dateTo}T23:59:59` }),
    }),
    [selectedMerchant, filters]
  );

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    const data = await getSmsUsageSummary(baseParams());
    setSummaryRows(data?.rows || []);
    setSummaryLoading(false);
  }, [baseParams]);

  const fetchLogs = useCallback(
    async (page = 1) => {
      setLogsLoading(true);
      const data = await getSmsLogs({
        page,
        limit: logFilters.limit,
        ...baseParams(),
        ...(logFilters.status && { status: logFilters.status }),
        ...(logFilters.purpose && { purpose: logFilters.purpose }),
        ...(logFilters.search && { search: logFilters.search }),
      });
      setLogs(data?.rows || []);
      setTotal(data?.total || 0);
      setTotalPages(data?.totalPages || 1);
      setLogsLoading(false);
    },
    [baseParams, logFilters]
  );

  useEffect(() => {
    if (tab === "summary") fetchSummary();
  }, [tab, fetchSummary]);

  useEffect(() => {
    if (tab === "logs") fetchLogs(currentPage);
  }, [tab, fetchLogs, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedMerchant, filters, logFilters]);

  // Debounced phone search for the logs tab.
  useEffect(() => {
    const t = setTimeout(() => {
      setLogFilters((prev) =>
        prev.search === searchText.trim() ? prev : { ...prev, search: searchText.trim() }
      );
    }, 400);
    return () => clearTimeout(t);
  }, [searchText]);

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const handleExportCsv = () => {
    exportToCsv(
      `sms-usage-${filters.dateFrom}-to-${filters.dateTo}.csv`,
      summaryRows,
      [
        { key: "month", label: "Month" },
        { key: (r) => r.merchantName || r.MerchantId || "—", label: "Merchant" },
        { key: "provider", label: "Provider" },
        { key: "sentCount", label: "Sent" },
        { key: "failedCount", label: "Failed" },
        { key: "noProviderCount", label: "No Provider" },
        { key: "testCount", label: "Test" },
        { key: "billableCount", label: "Billable" },
        { key: "totalCount", label: "Total" },
      ]
    );
  };

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page);
  };

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1).slice(
    Math.max(0, currentPage - 3),
    Math.min(totalPages, currentPage + 2)
  );

  const totals = summaryRows.reduce(
    (acc, r) => ({
      sent: acc.sent + (r.sentCount || 0),
      failed: acc.failed + (r.failedCount || 0),
      noProvider: acc.noProvider + (r.noProviderCount || 0),
      test: acc.test + (r.testCount || 0),
      billable: acc.billable + (r.billableCount || 0),
    }),
    { sent: 0, failed: 0, noProvider: 0, test: 0, billable: 0 }
  );

  return (
    <Fragment>
      <Breadcrumb title="SMS Usage" parent="Merchants" />
      <Container fluid={true}>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          <RCard>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 16,
                flexWrap: "wrap",
              }}
            >
              <button type="button" style={tabBtnStyle(tab === "summary")} onClick={() => setTab("summary")}>
                Summary
              </button>
              <button type="button" style={tabBtnStyle(tab === "logs")} onClick={() => setTab("logs")}>
                Message Logs
              </button>

              <IconBtn
                title="Refresh"
                Icon={RefreshCw}
                onClick={() => (tab === "summary" ? fetchSummary() : fetchLogs(currentPage))}
              />

              <select
                value={filters.provider}
                onChange={(e) => setFilter("provider", e.target.value)}
                style={filterSelectStyle}
              >
                {PROVIDER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>

              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilter("dateFrom", e.target.value)}
                style={filterInputStyle}
                title="From date"
              />
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilter("dateTo", e.target.value)}
                style={filterInputStyle}
                title="To date"
              />

              {tab === "logs" && (
                <>
                  <select
                    value={logFilters.status}
                    onChange={(e) => setLogFilters((prev) => ({ ...prev, status: e.target.value }))}
                    style={filterSelectStyle}
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={logFilters.purpose}
                    onChange={(e) => setLogFilters((prev) => ({ ...prev, purpose: e.target.value }))}
                    style={filterSelectStyle}
                  >
                    {PURPOSE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Search phone number..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ ...filterInputStyle, minWidth: 180 }}
                  />
                </>
              )}

              {tab === "summary" && summaryRows.length > 0 && (
                <IconBtn title="Export CSV" Icon={Download} onClick={handleExportCsv} />
              )}

              <span
                style={{
                  marginLeft: "auto",
                  fontSize: 12,
                  color: C.textLight,
                  background: C.bg,
                  padding: "4px 12px",
                  borderRadius: 20,
                }}
              >
                {tab === "summary"
                  ? `${totals.billable} billable messages`
                  : `${total} messages`}
              </span>
            </div>

            {tab === "summary" &&
              (summaryLoading ? (
                <RLoader />
              ) : (
                <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                  <table style={tbl.table}>
                    <thead>
                      <tr>
                        <th style={tbl.th}>Month</th>
                        <th style={tbl.th}>Merchant</th>
                        <th style={tbl.th}>Provider</th>
                        <th style={{ ...tbl.th, textAlign: "right" }}>Sent</th>
                        <th style={{ ...tbl.th, textAlign: "right" }}>Failed</th>
                        <th style={{ ...tbl.th, textAlign: "right" }}>No Provider</th>
                        <th style={{ ...tbl.th, textAlign: "right" }}>Test</th>
                        <th style={{ ...tbl.th, textAlign: "right" }}>Billable</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summaryRows.length > 0 ? (
                        <>
                          {summaryRows.map((r, i) => (
                            <tr key={`${r.month}-${r.MerchantId}-${r.provider}-${i}`} className="rpt-row">
                              <td style={{ ...tbl.td, fontFamily: "monospace", fontWeight: 600 }}>{r.month}</td>
                              <td style={{ ...tbl.td, fontWeight: 600 }}>
                                {r.merchantName || (r.MerchantId ? `Merchant #${r.MerchantId}` : "—")}
                              </td>
                              <td style={tbl.td}>{r.provider}</td>
                              <td style={{ ...tbl.td, textAlign: "right" }}>{r.sentCount}</td>
                              <td style={{ ...tbl.td, textAlign: "right", color: r.failedCount ? C.coral : C.textLight }}>
                                {r.failedCount}
                              </td>
                              <td style={{ ...tbl.td, textAlign: "right", color: r.noProviderCount ? C.orange : C.textLight }}>
                                {r.noProviderCount}
                              </td>
                              <td style={{ ...tbl.td, textAlign: "right", color: C.textMed }}>{r.testCount}</td>
                              <td style={{ ...tbl.td, textAlign: "right", fontWeight: 800, color: C.text }}>
                                {r.billableCount}
                              </td>
                            </tr>
                          ))}
                          <tr>
                            <td colSpan="3" style={{ ...tbl.td, fontWeight: 800, background: C.bg }}>
                              Total
                            </td>
                            <td style={{ ...tbl.td, textAlign: "right", fontWeight: 700, background: C.bg }}>{totals.sent}</td>
                            <td style={{ ...tbl.td, textAlign: "right", fontWeight: 700, background: C.bg }}>{totals.failed}</td>
                            <td style={{ ...tbl.td, textAlign: "right", fontWeight: 700, background: C.bg }}>{totals.noProvider}</td>
                            <td style={{ ...tbl.td, textAlign: "right", fontWeight: 700, background: C.bg }}>{totals.test}</td>
                            <td style={{ ...tbl.td, textAlign: "right", fontWeight: 800, background: C.bg }}>{totals.billable}</td>
                          </tr>
                        </>
                      ) : (
                        <tr>
                          <td colSpan="8" style={{ ...tbl.td, textAlign: "center", padding: 40, color: C.textLight }}>
                            No SMS usage in this period
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ))}

            {tab === "logs" &&
              (logsLoading ? (
                <RLoader />
              ) : (
                <>
                  <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                    <table style={tbl.table}>
                      <thead>
                        <tr>
                          <th style={tbl.th}>ID</th>
                          <th style={tbl.th}>Sent At</th>
                          <th style={tbl.th}>Merchant</th>
                          <th style={tbl.th}>Provider</th>
                          <th style={tbl.th}>Purpose</th>
                          <th style={tbl.th}>Status</th>
                          <th style={tbl.th}>Test</th>
                          <th style={tbl.th}>Receiver</th>
                          <th style={tbl.th}>Provider Msg ID</th>
                          <th style={tbl.th}>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.length > 0 ? (
                          logs.map((log) => (
                            <tr key={log.id} className="rpt-row">
                              <td style={{ ...tbl.td, fontWeight: 700, color: C.textMed }}>#{log.id}</td>
                              <td style={tbl.td}>{formatDate(log.createdAt)}</td>
                              <td style={{ ...tbl.td, fontWeight: 600 }}>
                                {log.Merchant?.name || (log.MerchantId ? `Merchant #${log.MerchantId}` : "—")}
                              </td>
                              <td style={tbl.td}>{log.provider}</td>
                              <td style={tbl.td}>{PURPOSE_LABEL[log.purpose] || log.purpose}</td>
                              <td style={tbl.td}>
                                <Pill status={log.status} />
                              </td>
                              <td style={tbl.td}>
                                {log.is_test ? (
                                  <span
                                    style={{
                                      padding: "3px 8px",
                                      borderRadius: 6,
                                      fontSize: 10,
                                      fontWeight: 700,
                                      background: C.blueLight,
                                      color: C.blue,
                                    }}
                                  >
                                    TEST
                                  </span>
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td style={{ ...tbl.td, fontFamily: "monospace", fontSize: 11 }}>
                                {log.receiverMobile || "—"}
                              </td>
                              <td style={{ ...tbl.td, fontFamily: "monospace", fontSize: 11, maxWidth: 140, wordBreak: "break-all" }}>
                                {log.providerMessageId || "—"}
                              </td>
                              <td style={{ ...tbl.td, maxWidth: 220, color: C.coral }}>{log.errorMessage || "—"}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="10" style={{ ...tbl.td, textAlign: "center", padding: 40, color: C.textLight }}>
                              No SMS logs found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {logs.length > 0 && totalPages > 1 && (
                    <Pagination className="d-flex justify-content-end mt-3 mb-0">
                      <PaginationItem disabled={currentPage <= 1}>
                        <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
                      </PaginationItem>
                      {pageNumbers.map((i) => (
                        <PaginationItem active={i === currentPage} key={i}>
                          <PaginationLink onClick={() => handlePageChange(i)}>{i}</PaginationLink>
                        </PaginationItem>
                      ))}
                      <PaginationItem disabled={currentPage >= totalPages}>
                        <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
                      </PaginationItem>
                    </Pagination>
                  )}
                </>
              ))}
          </RCard>
        </div>
      </Container>
    </Fragment>
  );
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "—";
  return (
    date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }) +
    ", " +
    date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    })
  );
};

export default SmsUsage;
